import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { RetentionRuleType } from '../../../../graphql/generated';
import {
  ActionsSection,
  ConditionBuilderSection,
  ManualReviewSection,
  NewRuleSection,
  RetentionPeriodSection,
} from '../../molecules';
import { BUTTON_LABELS, DRAWER_TITLES, RuleDrawerVariant } from '../../utils';
import { useAddRuleDrawer } from './useAddRuleDrawer';

interface AddRuleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  variant: RuleDrawerVariant;
  rule: RetentionRuleType | null;
  country?: string;
}

export const AddRuleDrawer: React.FC<AddRuleDrawerProps> = ({
  isOpen,
  onClose,
  variant = RuleDrawerVariant.NEW,
  rule,
  country,
}) => {
  const {
    control,
    handleSubmit,
    handleClose,
    ruleTypeOptions,
    countryOptions,
    isLoadingDropdown,
    isFormValid,
    isCreating,
    setValue,
    periodValue,
    periodUnit,
    anchorDate,
    conditionValueOptions,
    isLoadingConditionValues,
    showCountryError,
    showRuleTypeError,
  } = useAddRuleDrawer({ onClose, rule, variant, country, isOpen });

  const theme = useTheme<LensTheme>();
  const mode = useWatch({ control, name: 'mode' });

  const sp = theme.tokens.semantic.spacing.desktop.gap;
  const color = theme.tokens.semantic.color;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      ModalProps={{ keepMounted: false }}
      sx={{
        '& .MuiDrawer-paper': {
          width: '675px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: sp[3].value,
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: '30px',
            fontWeight: 600,
            color: color.type.default.value,
          }}
        >
          {DRAWER_TITLES[variant]}
        </Typography>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{
            color: color.action.secondary.onSecondary.value,
            padding: sp[2].value,
            '&:hover': {
              backgroundColor: color.action.secondary.hoverFill.value,
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: sp[4].value,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: sp[1].value,
          minHeight: 0,
        }}
      >
        {(variant === RuleDrawerVariant.NEW ||
          variant === RuleDrawerVariant.EDIT) && (
          <NewRuleSection
            control={control}
            ruleTypeOptions={ruleTypeOptions}
            countryOptions={countryOptions}
            isLoadingDropdown={isLoadingDropdown}
            showCountryError={showCountryError}
            showRuleTypeError={showRuleTypeError}
          />
        )}
        <ConditionBuilderSection
          control={control}
          variant={variant}
          conditionValueOptions={conditionValueOptions}
          isLoadingConditionValues={isLoadingConditionValues}
        />
        <RetentionPeriodSection
          control={control}
          mode={mode}
          setValue={setValue}
          periodValue={periodValue}
          periodUnit={periodUnit}
          anchorDate={anchorDate}
        />
        <ActionsSection control={control} />
        <ManualReviewSection control={control} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: sp[2].value,
            paddingTop: sp[4].value,
            marginTop: sp[2].value,
            marginBottom: sp[6].value,
            borderTop: `1px solid ${color.ui.divider.default.value}`,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              padding: `${sp[2].value} ${sp[3].value}`,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              boxShadow: 'none',
              backgroundColor: color.surface.default.value,
              color: color.action.secondary.onSecondary.value,
              border: `1px solid ${color.outline.default.value}`,
              '&:hover': {
                backgroundColor: color.action.secondary.hoverFill.value,
                boxShadow: 'none',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isCreating}
            sx={{
              padding: `${sp[2].value} ${sp[3].value}`,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              boxShadow: 'none',
              backgroundColor: color.action.primary.default.value,
              color: color.action.primary.onPrimary.value,
              border: `1px solid ${color.action.primary.default.value}`,
              '&:hover': {
                backgroundColor: color.action.primary.hover.value,
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: color.action.primary.disabled.value,
                color: color.action.primary.onPrimaryDisabled.value,
              },
            }}
          >
            {BUTTON_LABELS[variant][isCreating ? 'creating' : 'idle']}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
